import { Controller } from "@hotwired/stimulus";
import { createDescriptionEditor } from "../../discord_description_editor.js";

export default class extends Controller {
  static targets = [
    "tabPanel",
    "bodyTypeRadio",
    "plainPanel",
    "plainContent",
    "embedPanel",
    "embedTitle",
    "embedDescription",
    "embedUrl",
    "embedAuthorName",
    "embedAuthorUrl",
    "embedAuthorIconUrl",
    "embedFooterText",
    "embedFooterIconUrl",
    "embedThumbnail",
    "embedColorPicker",
    "embedColorInt",
    "embedColorLabel",
    "embedFieldsList",
    "embedImageUrl",
    "embedTimestamp",
    "rawJson",
    "hiddenJson",
    "preview",
    "nameInput",
    "nameCounter",
    "plainCounter",
    "descCounter",
    "embedTotalCounter",
    "saveBtn",
  ];

  static values = {
    noPreviewText: String,
    fieldInlineLabel: String,
    fieldNamePlaceholder: String,
    removeFieldAriaLabel: String,
    embedTitlePlaceholder: String,
    footerTextPlaceholder: String,
    embedTitleAriaLabel: String,
    embedDescriptionAriaLabel: String,
    embedFooterAriaLabel: String,
    toolbarBold: String,
    toolbarItalic: String,
    toolbarUnderline: String,
    toolbarStrikethrough: String,
    toolbarCode: String,
    toolbarLink: String,
    toolbarSpoiler: String,
    linkUrlAriaLabel: String,
    applyLinkAriaLabel: String,
    cancelAriaLabel: String,
  };

  connect() {
    this._descEditor = null;
    this._descOverLimit = false;
    this._titleOverLimit = false;
    this._authorOverLimit = false;
    this._footerOverLimit = false;
    this._combinedOverLimit = false;
    this._fieldEditors = new Map();
    const light = getComputedStyle(document.documentElement).colorScheme.includes("light");
    this._dc = light
      ? {
          bgOuter: "#ffffff",
          bgInner: "#f2f3f5",
          textMain: "#2e3338",
          textTitle: "#060607",
          textMuted: "#4e5058",
          textFieldName: "#060607",
          textFieldVal: "#2e3338",
          codeBg: "#e3e5e8",
          hoverBg: "rgba(0,0,0,0.06)",
          footerBorder: "#dfe1e4",
        }
      : {
          bgOuter: "#2b2d31",
          bgInner: "#2f3136",
          textMain: "#dbdee1",
          textTitle: "#f2f3f5",
          textMuted: "#949ba4",
          textFieldName: "#f2f3f5",
          textFieldVal: "#dbdee1",
          codeBg: "#1e1f22",
          hoverBg: "rgba(255,255,255,0.07)",
          footerBorder: "#3f4147",
        };
    if (light) this.previewTarget.classList.add("dc-light-preview");
    const type = this.currentBodyType();
    this.plainPanelTargets.forEach((el) => el.classList.toggle("hidden", type !== "text"));
    this.embedPanelTargets.forEach((el) => el.classList.toggle("hidden", type !== "embed"));
    if (!parseInt(this.embedColorIntTarget.value, 10)) {
      const hex = this.embedColorPickerTarget.value.replace(/^#/, "");
      this.embedColorIntTarget.value = parseInt(hex, 16) || 0;
    }
    this._updatePlainCounter();
    this._updateDescCounter();
    this.renderPreview();
    this._checkPerFieldLimits();
    this._updateCombinedCounter();
  }

  disconnect() {
    if (this._descEditor) {
      this._descEditor.setRootElement(null);
      this._descEditor = null;
    }
    this._fieldEditors?.forEach((editor) => editor.setRootElement(null));
    this._fieldEditors = null;
  }

  // ---- Tab switching ----

  switchTab({ currentTarget }) {
    const tab = currentTarget.dataset.tab;
    this.element.querySelectorAll("[data-tab-button]").forEach((btn) => {
      btn.classList.toggle("tab-active", btn.dataset.tab === tab);
      btn.setAttribute("aria-selected", String(btn.dataset.tab === tab));
    });
    this.tabPanelTargets.forEach((panel) => {
      panel.classList.toggle("hidden", panel.dataset.tab !== tab);
    });
    if (tab === "visual") this.renderPreview();
    if (tab === "embeds") this._mountFieldEditors();
  }

  // ---- Body type toggle ----

  switchBodyType({ target }) {
    const type = target.value;
    this.bodyTypeRadioTargets.forEach((r) =>
      r.closest("label")?.classList.toggle("sc-type-btn--active", r.value === type),
    );
    this.plainPanelTargets.forEach((el) => el.classList.toggle("hidden", type !== "text"));
    this.embedPanelTargets.forEach((el) => el.classList.toggle("hidden", type !== "embed"));
    if (type !== "embed") {
      this._descOverLimit = false;
      this._titleOverLimit = false;
      this._authorOverLimit = false;
      this._footerOverLimit = false;
      this._combinedOverLimit = false;
      this._updateSaveBtn();
    }
    this.syncRaw();
  }

  // ---- Visual → JSON ----

  syncRaw() {
    const json = this.buildJson();
    const formatted = JSON.stringify(json, null, 2);
    if (this.hasRawJsonTarget) this.rawJsonTarget.value = formatted;
    this.hiddenJsonTarget.value = JSON.stringify(json);
    this._updatePlainCounter();
    this._updateDescCounter();
    this._updateCombinedCounter();
    if (this._isVisualTabActive()) this.renderPreview();
  }

  syncColorToInt({ target }) {
    const hex = target.value.replace(/^#/, "");
    const intVal = parseInt(hex, 16) || 0;
    this.embedColorIntTarget.value = intVal;
    this.embedColorLabelTarget.textContent = target.value;
    this.syncRaw();
  }

  addEmbedField(event) {
    event.preventDefault();
    if (this.embedFieldsListTarget.querySelectorAll("[data-field-row]").length >= 25) return;
    this.embedFieldsListTarget.insertAdjacentHTML("beforeend", this.newFieldRowHtml());
    const newRow = this.embedFieldsListTarget.querySelector("[data-field-row]:last-child");
    this._mountFieldValueEditor(newRow);
    this.syncRaw();
  }

  removeEmbedField({ currentTarget }) {
    const row = currentTarget.closest("[data-field-row]");
    const container = row.querySelector("[data-field-value-editor]");
    if (container && this._fieldEditors?.has(container)) {
      this._fieldEditors.get(container).setRootElement(null);
      this._fieldEditors.delete(container);
    }
    row.remove();
    this.syncRaw();
  }

  // ---- Raw JSON → hidden input ----

  syncFromRaw() {
    try {
      const parsed = JSON.parse(this.rawJsonTarget.value);
      this.hiddenJsonTarget.value = JSON.stringify(parsed);
      if (this._isVisualTabActive()) this.renderPreview();
    } catch {
      // Leave hiddenJson as-is while JSON is being typed
    }
  }

  updateNameCounter({ target }) {
    if (this.hasNameCounterTarget) {
      this.nameCounterTarget.textContent = `${target.value.length} / 80`;
    }
  }

  // ---- Variable copy / insert / search ----

  copyVariable({ params: { snippet } }) {
    navigator.clipboard.writeText(snippet).catch(() => {});
  }

  insertVariable({ params: { snippet }, currentTarget: btn }) {
    const active = document.activeElement;
    const inserted =
      this._insertIntoTextarea(active, snippet) || this._insertIntoContenteditable(active, snippet);

    if (!inserted) {
      navigator.clipboard.writeText(snippet).catch(() => {});
    }
    this._flashBtn(btn);
  }

  prepareTestDialog() {
    const dialog = document.getElementById("test-send-dialog");
    const field = dialog?.querySelector("#test-send-body-json");
    if (field) field.value = this.hiddenJsonTarget.value;
    const errorEl = dialog?.querySelector("#test-send-error");
    if (errorEl) errorEl.classList.add("hidden");
  }

  onTestSendComplete({ detail: { success } }) {
    if (success) document.getElementById("test-send-dialog")?.close();
  }

  switchTestMethod({ target }) {
    const dialog = target.closest("dialog");
    const input = dialog?.querySelector("[data-test-target-input]");
    if (!input) return;
    if (target.value === "webhook") {
      input.name = "webhook_url";
      input.placeholder = "https://discord.com/api/webhooks/…";
    } else {
      input.name = "channel_id";
      input.placeholder = "123456789012345678";
    }
  }

  filterVariables({ target: { value } }) {
    const q = value.toLowerCase();
    this.element.querySelectorAll(".var-group").forEach((group) => {
      let visible = 0;
      group.querySelectorAll(".var-row").forEach((row) => {
        const match =
          !q ||
          (row.dataset.varName || "").includes(q) ||
          (row.dataset.varDesc || "").toLowerCase().includes(q);
        row.classList.toggle("hidden", !match);
        if (match) visible++;
      });
      group.classList.toggle("hidden", visible === 0);
    });
  }

  // ---- Per-field limit actions ----

  syncTitleInput({ target }) {
    this._checkInputLimit(target, 256, "_titleOverLimit");
    this.syncRaw();
  }

  syncFooterTextInput({ target }) {
    this._checkInputLimit(target, 2048, "_footerOverLimit");
    this.syncRaw();
  }

  syncAuthorNameInput({ target }) {
    this._checkInputLimit(target, 256, "_authorOverLimit");
    this.syncRaw();
  }

  // ---- Internal helpers ----

  buildJson() {
    const type = this.currentBodyType();
    if (type === "text") return { content: this.plainContentTarget.value };
    const embed = {};
    this._applyBasicEmbedFields(embed);
    const author = this._buildAuthorField();
    if (author) embed.author = author;
    const footer = this._buildFooterField();
    if (footer) embed.footer = footer;
    const thumb = this.embedThumbnailTarget.value.trim();
    if (thumb) embed.thumbnail = { url: thumb };
    const image = this.embedImageUrlTarget.value.trim();
    if (image) embed.image = { url: image };
    const fields = this.collectFields();
    if (fields.length) embed.fields = fields;
    const ts = this.hasEmbedTimestampTarget ? this.embedTimestampTarget.value.trim() : "";
    if (ts) embed.timestamp = ts;
    return embed;
  }

  _applyBasicEmbedFields(embed) {
    const title = this.embedTitleTarget.value.trim();
    if (title) embed.title = title;
    const desc = this.embedDescriptionTarget.value.trim();
    if (desc) embed.description = desc;
    const url = this.embedUrlTarget.value.trim();
    if (url) embed.url = url;
    const colorRaw = this.embedColorIntTarget.value.trim();
    const colorInt = parseInt(colorRaw, 10);
    if (colorRaw !== "" && !Number.isNaN(colorInt)) embed.color = colorInt;
  }

  _buildAuthorField() {
    const name = this.hasEmbedAuthorNameTarget ? this.embedAuthorNameTarget.value.trim() : "";
    const url = this.hasEmbedAuthorUrlTarget ? this.embedAuthorUrlTarget.value.trim() : "";
    const icon = this.hasEmbedAuthorIconUrlTarget ? this.embedAuthorIconUrlTarget.value.trim() : "";
    if (!name && !icon) return null;
    const author = { name };
    if (url) author.url = url;
    if (icon) author.icon_url = icon;
    return author;
  }

  _buildFooterField() {
    const text = this.embedFooterTextTarget.value.trim();
    const icon = this.hasEmbedFooterIconUrlTarget ? this.embedFooterIconUrlTarget.value.trim() : "";
    if (!text && !icon) return null;
    return { text, ...(icon ? { icon_url: icon } : {}) };
  }

  collectFields() {
    const rows = this.embedFieldsListTarget.querySelectorAll("[data-field-row]");
    const fields = [];
    rows.forEach((row) => {
      const name = row.querySelector("[data-field-name]")?.value || "";
      const value = row.querySelector("[data-field-value]")?.value || "";
      const inline = row.querySelector("[data-field-inline]")?.checked || false;
      if (name || value) fields.push({ name, value, inline });
    });
    return fields;
  }

  #editorStrings() {
    const defaults = {
      bold: "Bold",
      italic: "Italic",
      underline: "Underline",
      strikethrough: "Strikethrough",
      code: "Inline code",
      link: "Link",
      spoiler: "Spoiler",
      linkUrl: "Link URL",
      applyLink: "Apply link",
      cancel: "Cancel",
    };
    const values = {
      bold: this.toolbarBoldValue,
      italic: this.toolbarItalicValue,
      underline: this.toolbarUnderlineValue,
      strikethrough: this.toolbarStrikethroughValue,
      code: this.toolbarCodeValue,
      link: this.toolbarLinkValue,
      spoiler: this.toolbarSpoilerValue,
      linkUrl: this.linkUrlAriaLabelValue,
      applyLink: this.applyLinkAriaLabelValue,
      cancel: this.cancelAriaLabelValue,
    };
    const result = {};
    for (const key of Object.keys(defaults)) {
      result[key] = values[key] || defaults[key];
    }
    return result;
  }

  currentBodyType() {
    const checked = this.bodyTypeRadioTargets.find((r) => r.checked);
    return checked ? checked.value : "text";
  }

  newFieldRowHtml() {
    const inline = this.escHtml(this.fieldInlineLabelValue || "Inline");
    const namePh = this.escHtml(this.fieldNamePlaceholderValue || "Field name");
    const removeLabel = this.escHtml(this.removeFieldAriaLabelValue || "Remove field");
    return `
      <div class="flex gap-2 items-start mb-2" data-field-row>
        <input type="text" class="input input-bordered input-xs flex-1"
               placeholder="${namePh}" data-field-name maxlength="256"
               data-action="input->message-editor#syncRaw">
        <div class="flex flex-col flex-1 min-w-0">
          <div class="dc-field-value-editor" contenteditable="true" data-field-value-editor></div>
          <input type="hidden" value="" data-field-value>
          <div class="dc-field-value-counter" data-field-value-counter>0 / 1024</div>
        </div>
        <label class="flex items-center gap-1 text-xs">
          <input type="checkbox" class="checkbox checkbox-xs" data-field-inline
                 data-action="change->message-editor#syncRaw"> ${inline}
        </label>
        <button type="button" class="btn btn-xs btn-ghost text-error"
                data-action="click->message-editor#removeEmbedField"
                aria-label="${removeLabel}">
          <i class="fa-solid fa-trash"></i>
        </button>
      </div>`.trim();
  }

  renderPreview() {
    let json;
    try {
      json = JSON.parse(this.rawJsonTarget.value);
    } catch {
      json = {};
    }
    if (json.content) {
      this.previewTarget.innerHTML = this.#discordMessage(json.content);
    } else if (this.currentBodyType() === "embed") {
      this.previewTarget.innerHTML = this.#discordEmbed(json);
      for (const sel of [
        "[data-preview-thumb]",
        "[data-preview-image]",
        "[data-preview-author-icon]",
        "[data-preview-footer-icon]",
      ]) {
        const el = this.previewTarget.querySelector(sel);
        if (el)
          el.addEventListener(
            "error",
            () => {
              el.style.display = "none";
            },
            { once: true },
          );
      }
      this._wirePreviewInputs();
    } else {
      const text = this.hasNoPreviewTextValue ? this.noPreviewTextValue : "Nothing to preview.";
      this.previewTarget.innerHTML = `<p class="text-xs text-base-content/50">${this.escHtml(text)}</p>`;
    }
  }

  #discordMessage(content) {
    const { bgOuter, textMain, codeBg } = this._dc;
    const text = this.escHtml(content)
      .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
      .replace(/\*(.+?)\*/g, "<em>$1</em>")
      .replace(
        /`(.+?)`/g,
        `<code style="background:${codeBg};padding:1px 4px;border-radius:3px;font-size:12px;font-family:monospace">$1</code>`,
      );
    return `<div style="background:${bgOuter};border-radius:4px;padding:12px 16px;font-size:14px;color:${textMain};line-height:1.375;white-space:pre-wrap;font-family:sans-serif">${text}</div>`;
  }

  #discordEmbed(embed) {
    const { bgOuter, bgInner } = this._dc;
    const color =
      embed.color !== undefined ? "#" + embed.color.toString(16).padStart(6, "0") : "#1e1f22";
    const s = (str) => this.escHtml(str ?? "");
    const thumbUrl = this.#safeImgUrl(embed.thumbnail?.url);
    const thumb = thumbUrl
      ? `<img src="${thumbUrl}" data-preview-thumb style="width:80px;height:80px;object-fit:cover;border-radius:4px;flex-shrink:0;margin-left:16px;align-self:flex-start" alt="">`
      : "";
    const imageUrl = this.#safeImgUrl(embed.image?.url);
    const imageHtml = imageUrl
      ? `<img src="${imageUrl}" data-preview-image style="width:100%;border-radius:4px;margin-top:8px;display:block" alt="">`
      : "";
    const body =
      this.#embedAuthorHtml(embed, s) +
      this.#embedTitleHtml(embed, s) +
      this.#embedDescHtml(embed) +
      this.#embedFieldsHtml(embed, s) +
      imageHtml +
      this.#embedFooterHtml(embed, s);
    return `<div style="background:${bgOuter};border-radius:4px;padding:8px;font-family:sans-serif;overflow:hidden">
      <div style="display:flex;align-items:flex-start;background:${bgInner};border-radius:4px;border-left:4px solid ${color};padding:12px;overflow:hidden">
        <div style="flex:1;min-width:0;overflow:hidden;word-break:break-word;overflow-wrap:anywhere">${body}</div>
        ${thumb}
      </div>
    </div>`;
  }

  #embedAuthorHtml(embed, s) {
    if (!embed.author?.name) return "";
    const iconUrl = this.#safeImgUrl(embed.author.icon_url);
    const iconHtml = iconUrl
      ? `<img src="${iconUrl}" data-preview-author-icon style="width:24px;height:24px;border-radius:50%;object-fit:cover" alt="">`
      : "";
    return `<div style="display:flex;align-items:center;gap:8px;margin-bottom:8px">${iconHtml}<span style="color:${this._dc.textTitle};font-size:14px;font-weight:600">${s(embed.author.name)}</span></div>`;
  }

  #embedTitleHtml(embed, s) {
    const val = s(embed.title ?? "");
    const ph = this.#escOr(this.embedTitlePlaceholderValue, "Embed title");
    const label = this.#escOr(this.embedTitleAriaLabelValue, "Title");
    if (embed.url) {
      return `<div style="margin-bottom:4px"><input type="text" data-preview-title class="dc-preview-title dc-preview-title--linked" value="${val}" placeholder="${ph}" aria-label="${label}" maxlength="256" spellcheck="false"></div>`;
    }
    return `<input type="text" data-preview-title class="dc-preview-title" value="${val}" placeholder="${ph}" aria-label="${label}" maxlength="256" spellcheck="false" style="margin-bottom:4px">`;
  }

  #embedDescHtml(embed) {
    const descLen = (embed.description || "").length;
    const counterClass = `dc-preview-desc-counter${descLen > 4096 ? " dc-preview-desc-counter--over" : ""}`;
    const label = this.#escOr(this.embedDescriptionAriaLabelValue, "Description");
    return `<div data-preview-desc-editor contenteditable="true" class="dc-preview-desc dc-preview-desc--lexical" spellcheck="false" role="textbox" aria-multiline="true" aria-label="${label}"></div><div data-preview-desc-counter class="${counterClass}">${descLen} / 4096</div>`;
  }

  #embedFooterHtml(embed, s) {
    let tsDisplay = "";
    if (embed.timestamp) {
      const d = new Date(embed.timestamp);
      tsDisplay = !isNaN(d) ? s(d.toLocaleString()) : s(embed.timestamp);
    }
    const footerIconUrl = this.#safeImgUrl(embed.footer?.icon_url);
    const iconHtml = footerIconUrl
      ? `<img src="${footerIconUrl}" data-preview-footer-icon style="width:20px;height:20px;border-radius:50%;object-fit:cover;flex-shrink:0" alt="">`
      : "";
    const tsHtml = tsDisplay
      ? `<span style="color:${this._dc.textMuted};font-size:12px;flex-shrink:0">${tsDisplay.includes("•") ? "" : "• "}${tsDisplay}</span>`
      : "";
    const footerPh = this.#escOr(this.footerTextPlaceholderValue, "Footer text…");
    const footerLabel = this.#escOr(this.embedFooterAriaLabelValue, "Footer text");
    return `<div style="display:flex;align-items:center;gap:8px;margin-top:8px;flex-wrap:wrap">${iconHtml}<input type="text" data-preview-footer class="dc-preview-footer" value="${s(embed.footer?.text ?? "")}" placeholder="${footerPh}" aria-label="${footerLabel}" maxlength="2048" spellcheck="false" style="flex:1;min-width:0">${tsHtml}</div>`;
  }

  #escOr(value, fallback) {
    return this.escHtml(value || fallback);
  }

  #embedFieldsHtml(embed, s) {
    if (!embed.fields?.length) return "";
    const items = embed.fields
      .map((f) => {
        const flex = f.inline ? "1 1 calc(33% - 4px)" : "1 1 100%";
        const maxW = f.inline ? "calc(33% - 4px)" : "100%";
        return `<div style="min-width:0;flex:${flex};max-width:${maxW}">
        <div style="color:${this._dc.textFieldName};font-size:12px;font-weight:700;margin-bottom:2px">${s(f.name) || "​"}</div>
        <div style="color:${this._dc.textFieldVal};font-size:14px;line-height:1.375">${s(f.value) || "​"}</div>
      </div>`;
      })
      .join("");
    return `<div style="display:flex;flex-wrap:wrap;gap:8px;margin-top:8px">${items}</div>`;
  }

  escHtml(str) {
    return String(str ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  // Discord embed image/icon URLs are user-entered; only allow http(s) so a
  // javascript:/data: value can never reach an <img src="...">.
  #safeImgUrl(str) {
    const trimmed = String(str ?? "").trim();
    return /^https?:\/\//i.test(trimmed) ? this.escHtml(trimmed) : "";
  }

  _checkInputLimit(el, limit, flagName) {
    const over = el.value.length > limit;
    const wasOver = this[flagName];
    this[flagName] = over;
    const errorClass = el.tagName === "TEXTAREA" ? "textarea-error" : "input-error";
    el.classList.toggle(errorClass, over);
    if (over && !wasOver) {
      el.classList.remove("dc-input--shake");
      requestAnimationFrame(() => el.classList.add("dc-input--shake"));
    }
    this._updateSaveBtn();
  }

  _checkPerFieldLimits() {
    if (this.hasEmbedTitleTarget) {
      this._titleOverLimit = this.embedTitleTarget.value.length > 256;
      this.embedTitleTarget.classList.toggle("input-error", this._titleOverLimit);
    }
    if (this.hasEmbedAuthorNameTarget) {
      this._authorOverLimit = this.embedAuthorNameTarget.value.length > 256;
      this.embedAuthorNameTarget.classList.toggle("input-error", this._authorOverLimit);
    }
    if (this.hasEmbedFooterTextTarget) {
      this._footerOverLimit = this.embedFooterTextTarget.value.length > 2048;
      this.embedFooterTextTarget.classList.toggle("textarea-error", this._footerOverLimit);
    }
  }

  _computeCombinedLength() {
    const title = this.hasEmbedTitleTarget ? this.embedTitleTarget.value.length : 0;
    const desc = this.hasEmbedDescriptionTarget ? this.embedDescriptionTarget.value.length : 0;
    const author = this.hasEmbedAuthorNameTarget ? this.embedAuthorNameTarget.value.length : 0;
    const footer = this.hasEmbedFooterTextTarget ? this.embedFooterTextTarget.value.length : 0;
    const fields = this.hasEmbedFieldsListTarget
      ? [...this.embedFieldsListTarget.querySelectorAll("[data-field-row]")].reduce((sum, row) => {
          return (
            sum +
            (row.querySelector("[data-field-name]")?.value.length || 0) +
            (row.querySelector("[data-field-value]")?.value.length || 0)
          );
        }, 0)
      : 0;
    return title + desc + author + footer + fields;
  }

  _updateCombinedCounter() {
    const total = this._computeCombinedLength();
    const over = total > 6000;
    if (over && !this._combinedOverLimit) {
      const activeEl = this.element.contains(document.activeElement)
        ? document.activeElement
        : null;
      if (activeEl) {
        activeEl.classList.remove("dc-input--shake");
        requestAnimationFrame(() => activeEl.classList.add("dc-input--shake"));
      }
    }
    this._combinedOverLimit = over;
    if (this.hasEmbedTotalCounterTarget) {
      this.embedTotalCounterTarget.textContent = `${total} / 6000`;
      this.embedTotalCounterTarget.classList.toggle("text-error", over);
    }
    this._updateSaveBtn();
  }

  _updatePlainCounter() {
    if (!this.hasPlainCounterTarget || !this.hasPlainContentTarget) return;
    const len = this.plainContentTarget.value.length;
    this.plainCounterTarget.textContent = `${len} / 2000`;
    this.plainCounterTarget.classList.toggle("text-error", len > 2000);
  }

  _updateDescCounter(len) {
    if (!this.hasDescCounterTarget) return;
    const actualLen =
      len ?? (this.hasEmbedDescriptionTarget ? this.embedDescriptionTarget.value.length : 0);
    this.descCounterTarget.textContent = `${actualLen} / 4096`;
    const over = actualLen > 4096;
    this.descCounterTarget.classList.toggle("text-error", over);
    if (this.hasEmbedDescriptionTarget) {
      this.embedDescriptionTarget.classList.toggle("textarea-error", over);
    }
  }

  _isVisualTabActive() {
    const panel = this.tabPanelTargets.find((p) => p.dataset.tab === "visual");
    return panel !== null && panel !== undefined && !panel.classList.contains("hidden");
  }

  _syncJsonOnly() {
    const json = this.buildJson();
    const formatted = JSON.stringify(json, null, 2);
    if (this.hasRawJsonTarget) this.rawJsonTarget.value = formatted;
    this.hiddenJsonTarget.value = JSON.stringify(json);
    this._updateCombinedCounter();
  }

  _updateSaveBtn() {
    if (!this.hasSaveBtnTarget) return;
    const over =
      this._descOverLimit ||
      this._combinedOverLimit ||
      this._titleOverLimit ||
      this._authorOverLimit ||
      this._footerOverLimit;
    this.saveBtnTarget.disabled = over;
    this.saveBtnTarget.classList.toggle("btn-disabled", over);
  }

  _wirePreviewInputs() {
    this._wireTitleInput();
    this._wireFooterInput();
    this._mountDescEditor();
  }

  _wireTitleInput() {
    const titleInput = this.previewTarget.querySelector("[data-preview-title]");
    if (!titleInput) return;
    titleInput.addEventListener("input", (e) => {
      if (this.hasEmbedTitleTarget) this.embedTitleTarget.value = e.target.value;
      this._checkInputLimit(e.target, 256, "_titleOverLimit");
      this._syncJsonOnly();
    });
  }

  _wireFooterInput() {
    const footerInput = this.previewTarget.querySelector("[data-preview-footer]");
    if (!footerInput) return;
    footerInput.addEventListener("input", (e) => {
      if (this.hasEmbedFooterTextTarget) this.embedFooterTextTarget.value = e.target.value;
      this._syncJsonOnly();
    });
  }

  _mountFieldEditors() {
    if (!this.hasEmbedFieldsListTarget) return;
    this.embedFieldsListTarget.querySelectorAll("[data-field-row]").forEach((row) => {
      const container = row.querySelector("[data-field-value-editor]");
      if (container && !this._fieldEditors.has(container)) this._mountFieldValueEditor(row);
    });
  }

  _mountFieldValueEditor(row) {
    const container = row.querySelector("[data-field-value-editor]");
    if (!container) return;
    const hiddenInput = row.querySelector("[data-field-value]");
    const counter = row.querySelector("[data-field-value-counter]");
    const initial = hiddenInput?.value || "";
    let initialFired = false;
    const editor = createDescriptionEditor(
      container,
      initial,
      (md) => {
        if (!initialFired) {
          initialFired = true;
          return;
        }
        if (hiddenInput) hiddenInput.value = md;
        const over = md.length > 1024;
        container.classList.toggle("dc-field-value-editor--over-limit", over);
        if (counter) {
          counter.textContent = `${md.length} / 1024`;
          counter.classList.toggle("dc-field-value-counter--over", over);
        }
        this._updateCombinedCounter();
        if (!over) this.syncRaw();
      },
      {
        maxLength: 1024,
        onAtLimit: () => {
          container.classList.remove("dc-field-value-editor--shake");
          requestAnimationFrame(() => container.classList.add("dc-field-value-editor--shake"));
        },
        strings: this.#editorStrings(),
      },
    );
    this._fieldEditors.set(container, editor);
  }

  _mountDescEditor() {
    const container = this.previewTarget.querySelector("[data-preview-desc-editor]");
    if (!container) return;
    if (this._descEditor) this._descEditor.setRootElement(null);
    const initial = this.hasEmbedDescriptionTarget ? this.embedDescriptionTarget.value : "";
    const previewCounter = this.previewTarget.querySelector("[data-preview-desc-counter]");

    // Apply initial state synchronously so switching tabs restores the warning correctly.
    const initOverLimit = initial.length > 4096;
    container.classList.toggle("dc-preview-desc--over-limit", initOverLimit);
    if (previewCounter) {
      previewCounter.textContent = `${initial.length} / 4096`;
      previewCounter.classList.toggle("dc-preview-desc-counter--over", initOverLimit);
    }
    this._updateDescCounter(initial.length);
    this._descOverLimit = initOverLimit;
    this._updateSaveBtn();

    let mountedEditor;
    let initialFired = false;
    mountedEditor = createDescriptionEditor(
      container,
      initial,
      (md) => {
        if (this._descEditor !== mountedEditor) return;
        if (!initialFired) {
          initialFired = true;
          return;
        }
        const overLimit = md.length > 4096;
        container.classList.toggle("dc-preview-desc--over-limit", overLimit);
        if (previewCounter) {
          previewCounter.textContent = `${md.length} / 4096`;
          previewCounter.classList.toggle("dc-preview-desc-counter--over", overLimit);
        }
        this._updateDescCounter(md.length);
        this._descOverLimit = overLimit;
        this._updateSaveBtn();
        if (this.hasEmbedDescriptionTarget) this.embedDescriptionTarget.value = md;
        this._updateCombinedCounter();
        if (!overLimit) this._syncJsonOnly();
      },
      {
        maxLength: 4096,
        onAtLimit: () => {
          container.classList.remove("dc-preview-desc--shake");
          requestAnimationFrame(() => container.classList.add("dc-preview-desc--shake"));
        },
        strings: this.#editorStrings(),
      },
    );
    this._descEditor = mountedEditor;
  }

  _insertIntoTextarea(el, snippet) {
    if (!el || (el.tagName !== "TEXTAREA" && el.tagName !== "INPUT")) return false;
    if (!this.element.contains(el)) return false;
    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? el.value.length;
    el.setRangeText(snippet, start, end, "end");
    el.dispatchEvent(new Event("input", { bubbles: true }));
    return true;
  }

  _insertIntoContenteditable(el, snippet) {
    if (!el || el.contentEditable !== "true") return false;
    if (!this.element.contains(el)) return false;
    // execCommand is deprecated but remains the most reliable cross-browser
    // way to insert text into a contenteditable without disrupting Lexical state.
    document.execCommand("insertText", false, snippet);
    return true;
  }

  _flashBtn(btn) {
    const icon = btn.querySelector("i");
    if (!icon) return;
    icon.className = "fa-solid fa-check";
    setTimeout(() => {
      icon.className = "fa-solid fa-arrow-right-to-bracket";
    }, 1200);
  }
}
